import { useMemo, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type ChipSelectorProps<T, Id> = {
  options: T[];
  optionId: keyof T & string;
  optionLabel: keyof T & string;
  value: Id[];
  onChange(value: Id[]): void;
  filterBy?: (keyof T & string) | (keyof T & string)[];

  // passed along to MultiSelect
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  filter?: boolean;
  onFilter?: (event: { filter: string }) => void;
  virtualScrollerOptions?: {
    itemSize?: number;
    lazy?: boolean;
    onLazyLoad?: (event: { first?: number; last?: number }) => void;
  };
  scrollHeight?: string;
  maxSelectedLabels?: number;
  showClearAll?: boolean;
  clearAllLabel?: string;
  emptyActionLabel?: string;
  onClickEmptyAction?: () => void;
};

export function ChipSelector<T, Id extends string | number>(
  props: ChipSelectorProps<T, Id>,
) {
  const [open, setOpen] = useState(false);
  const [localFilter, setLocalFilter] = useState('');

  const optionLabelById = useMemo(() => {
    const map = new Map<Id, string>();
    for (const option of props.options) {
      map.set(
        option[props.optionId] as Id,
        String(option[props.optionLabel] ?? ''),
      );
    }
    return map;
  }, [props.optionId, props.optionLabel, props.options]);

  const normalizedFilter = localFilter.trim().toLowerCase();
  const visibleOptions = useMemo(() => {
    if (!normalizedFilter) return props.options;
    if (props.filterBy) {
      const keys = Array.isArray(props.filterBy)
        ? props.filterBy
        : [props.filterBy];
      return props.options.filter((option) =>
        keys.some((key) =>
          String(option[key] ?? '')
            .toLowerCase()
            .includes(normalizedFilter),
        ),
      );
    }
    return props.options.filter((option) =>
      String(option[props.optionLabel] ?? '')
        .toLowerCase()
        .includes(normalizedFilter),
    );
  }, [normalizedFilter, props.filterBy, props.optionLabel, props.options]);

  const selectedValueSet = useMemo(() => new Set(props.value), [props.value]);
  const selectedLabels = useMemo(
    () => props.value.map((id) => optionLabelById.get(id) ?? String(id)),
    [optionLabelById, props.value],
  );
  const maxLabels = props.maxSelectedLabels ?? 2;
  const triggerText = selectedLabels.length
    ? selectedLabels.length <= maxLabels
      ? selectedLabels.join(', ')
      : `${selectedLabels.slice(0, maxLabels).join(', ')} +${selectedLabels.length - maxLabels}`
    : (props.placeholder ?? 'Select');

  const toggleOption = (id: Id) => {
    const next = selectedValueSet.has(id)
      ? props.value.filter((value) => value !== id)
      : [...props.value, id];
    props.onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            disabled={props.disabled}
            className={cn(
              'h-8 w-full justify-between rounded-md border-slate-300 bg-white px-2.5 text-left text-sm font-normal',
              props.className,
            )}
            style={props.style}
          >
            <span className="truncate">{triggerText}</span>
            <ChevronDownIcon className="size-4 text-slate-500" />
          </Button>
        }
      />
      <PopoverContent align="start" className="w-(--anchor-width) gap-2 p-2">
        {props.showClearAll && props.value.length > 0 && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                props.onChange([]);
              }}
            >
              {props.clearAllLabel ?? 'Clear all'}
            </Button>
          </div>
        )}
        {(props.filter ?? !!props.filterBy) && (
          <Input
            value={localFilter}
            placeholder={props.placeholder ?? 'Search'}
            onChange={(event) => {
              const next = event.target.value;
              setLocalFilter(next);
              props.onFilter?.({ filter: next });
            }}
          />
        )}
        <div
          className="overflow-y-auto rounded border border-slate-200"
          style={{ maxHeight: props.scrollHeight ?? '320px' }}
          onScroll={(event) => {
            const target = event.currentTarget;
            if (
              !props.virtualScrollerOptions?.lazy ||
              !props.virtualScrollerOptions.onLazyLoad
            ) {
              return;
            }
            const threshold = 48;
            const nearBottom =
              target.scrollHeight - target.scrollTop - target.clientHeight <=
              threshold;
            if (!nearBottom) return;
            const itemSize = props.virtualScrollerOptions.itemSize ?? 32;
            const last = Math.ceil(
              (target.scrollTop + target.clientHeight) / itemSize,
            );
            props.virtualScrollerOptions.onLazyLoad({
              first: Math.floor(target.scrollTop / itemSize),
              last,
            });
          }}
        >
          {props.loading ? (
            <div className="p-2 text-sm text-slate-500">Loading...</div>
          ) : visibleOptions.length === 0 ? (
            <div className="p-2 text-sm text-slate-500">
              <div>No options</div>
              {props.emptyActionLabel && props.onClickEmptyAction && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-7 px-2 text-xs"
                  onClick={props.onClickEmptyAction}
                >
                  {props.emptyActionLabel}
                </Button>
              )}
            </div>
          ) : (
            visibleOptions.map((option) => {
              const id = option[props.optionId] as Id;
              const label = String(option[props.optionLabel] ?? id);
              const checked = selectedValueSet.has(id);
              return (
                <label
                  key={String(id)}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleOption(id)}
                  />
                  <span className="truncate">{label}</span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
